import { useEffect, useRef } from 'react';
import { i18n } from '../../../../i18n';
import { detectQrFrame } from './detectQrFrame';

const SCAN_INTERVAL_MS = 200;

type UsePairingCameraOptions = {
  active: boolean;
  /** Bump to unlock after a failed approve so another scan can run. */
  resetKey?: number;
  /** Return true to stop scanning (valid code accepted). */
  onFrame: (raw: string) => boolean;
  onError: (message: string) => void;
};

function cameraErrorMessage(err: unknown): string {
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return i18n.t('displays:pairing.camera.insecure');
  }
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      return i18n.t('displays:pairing.camera.denied');
    }
    if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      return i18n.t('displays:pairing.camera.notFound');
    }
    if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
      return i18n.t('displays:pairing.camera.inUse');
    }
  }
  return i18n.t('displays:pairing.camera.generic');
}

/**
 * Opens the rear camera while `active`, polls frames for a QR, and tears down on unmount/close.
 */
export function usePairingCamera({
  active,
  resetKey = 0,
  onFrame,
  onError,
}: UsePairingCameraOptions) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onFrameRef = useRef(onFrame);
  const onErrorRef = useRef(onError);
  onFrameRef.current = onFrame;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let stream: MediaStream | null = null;
    let intervalId = 0;
    let locked = false;
    const canvas = document.createElement('canvas');

    const stopTracks = () => {
      stream?.getTracks().forEach((track) => track.stop());
      stream = null;
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };

    const poll = () => {
      if (cancelled || locked) return;
      const video = videoRef.current;
      if (!video) return;
      void detectQrFrame(video, canvas).then((raw) => {
        if (cancelled || locked || !raw) return;
        if (onFrameRef.current(raw)) {
          locked = true;
        }
      });
    };

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        onErrorRef.current(i18n.t('displays:pairing.camera.unavailable'));
        return;
      }
      if (!window.isSecureContext) {
        onErrorRef.current(cameraErrorMessage(null));
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        await video.play();
        if (cancelled) {
          stopTracks();
          return;
        }
        intervalId = window.setInterval(poll, SCAN_INTERVAL_MS);
      } catch (err) {
        if (!cancelled) onErrorRef.current(cameraErrorMessage(err));
      }
    };

    void start();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      stopTracks();
    };
  }, [active, resetKey]);

  return { videoRef };
}
